import { BloodSampleNames } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/types';
import { DisplayedPerfusionTerminationType } from '@features/components/ClinicalData/types';
import {
  BloodFromType,
  ClinicalItemType,
  LiverAnatomyType,
  PerfusionTerminationType,
  RadiometerType,
  WOLSType,
} from '@/api/apis/case';

export const CLINICAL_ITEM_TYPE_TO_TEXT: Record<ClinicalItemType, string> = {
  [ClinicalItemType.NRPStart]: 'In-situ perfusion start',
  [ClinicalItemType.ScheduledDonorORStartTime]: 'Scheduled Donor OR start time',
  [ClinicalItemType.DonorInOR]: 'Donor in OR',
  [ClinicalItemType.RecordDelay]: 'Delay',
  [ClinicalItemType.Heparin]: 'Heparin',
  [ClinicalItemType.SkinIncisionTime]: 'Skin incision time',
  [ClinicalItemType.CrossClampTimeFlushTime]: 'Cross clamp time',
  [ClinicalItemType.FinalFlush]: 'Final flush',
  [ClinicalItemType.DonorDidNotExpireInTime]: 'Donor did not expire at time',
  [ClinicalItemType.DryRun]: 'Dry Run',
  [ClinicalItemType.Turndown]: 'Turndown',
  [ClinicalItemType.OneLungTurndown]: 'One Lung Turndown',
  [ClinicalItemType.OCSDeviceDeployment]: 'OCS Device deployment',
  [ClinicalItemType.DeployedToDonorHospital]: 'Deployed to Donor Hospital',
  [ClinicalItemType.AtDonorHospital]: 'At Donor Hospital',
  [ClinicalItemType.OrganOnOCS]: 'Organ on OCS',
  [ClinicalItemType.LeavingDonorHospital]: 'Leaving Donor Hospital',
  [ClinicalItemType.ArrivalAtRecipientHospital]: 'Arrival at recipient hospital',
  [ClinicalItemType.OrganAccepted]: 'Request organ approval',
  [ClinicalItemType.LeavingRecipientHospital]: 'Leaving recipient hospital',
  [ClinicalItemType.SpecialistIsDone]: 'Specialist is done',
  [ClinicalItemType.PFOValveCondition]: 'PFO/Valve condition',
  [ClinicalItemType.OffOCS]: 'Organ off OCS',
  [ClinicalItemType.Priming]: 'Priming',
  [ClinicalItemType.CardiacArrest]: 'Cardiac arrest',
  [ClinicalItemType.PreOCSFlush]: 'Pre OCS flush',
  [ClinicalItemType.AtHub]: 'At Hub',
  [ClinicalItemType.CheckInOCSDevice]: 'Check inOCSDevice',
  [ClinicalItemType.BloodAvailabilityCheck]: 'Blood check availability',
  [ClinicalItemType.DonorLabelsCheck]: 'Donor labels check',
  [ClinicalItemType.BronchCartAndSuctionCheck]: 'Bronch cart and suction check',
  [ClinicalItemType.ReintubationPersonalCheck]: 'Reintubation personal check',
  [ClinicalItemType.OnSiteCoordinatorsContacts]: 'On-site coordinators contacts',
  [ClinicalItemType.PickUpOfOrganLabelAndTissueSamples]: 'Organ label and tissue samples',
  [ClinicalItemType.IceForTheOCSCoolerPriorDeparture]: 'Ice for the OCS cooler',
  [ClinicalItemType.CysticDuct]: 'Cystic duct',
  [ClinicalItemType.ResearchBloodSamples]: 'Research blood sample',
  [ClinicalItemType.TeamHuddle]: 'Team Huddle',
  [ClinicalItemType.WithdrawalOfLifeSupport]: 'Withdrawal of life support',
  [ClinicalItemType.WithdrawalOfLifeSupportTime]: 'WOLS Time',
  [ClinicalItemType.NRPApplicabilityAndType]: 'NRP applicability and type',
  [ClinicalItemType.DeathDeclarationTime]: 'Death declaration time',
  [ClinicalItemType.PostFlushLungsPhoto]: 'Post flush lungs photo',
  [ClinicalItemType.Stabilization]: 'Stabilization',
  [ClinicalItemType.PreOCSBloodSample]: 'Pre OCS Blood Sample',
  [ClinicalItemType.BloodSample]: 'Blood Sample',
  [ClinicalItemType.LiverOnABackBench]: 'Liver on a back bench',
  [ClinicalItemType.AnatomyPhotos]: 'Anatomy photos',
  [ClinicalItemType.PostFlushBronch]: 'Post flush bronch',
  [ClinicalItemType.BronchInSitu]: 'Bronch in situ',
  [ClinicalItemType.BronchVideoOnTheOCS]: 'Bronch video on the OCS',
  [ClinicalItemType.PFRatioInSitu]: 'PF Ratio in Situ',
  [ClinicalItemType.PFRatioOnOCSDonorHospital]: 'PF ratio on the OCS',
  [ClinicalItemType.PFRatioOnOCSTXCenter]: 'PF ratio on the OCS (TX Center)',
  [ClinicalItemType.FinalBronchVideoAtTheRecipientHospital]: 'Final Bronch Video At The TX Center',
  [ClinicalItemType.OffOCSPhotoAndVideo]: 'Off OCS photo and video',
  [ClinicalItemType.OnOCSVideo]: 'On OCS video',
  [ClinicalItemType.InSituVideoOfHeart]: 'In situ video of heart',
  [ClinicalItemType.VisualInspection]: 'Visual inspection',
  [ClinicalItemType.FinalPhysicalExamPictureOrVideo]: 'Final physical exam',
  [ClinicalItemType.FinalCheck]: 'Final check',
  [ClinicalItemType.ReconstructionVideosPhotos]: 'Reconstruction videos, photos',
  [ClinicalItemType.DonorBiopsyReport]: 'Donor biopsy report',
  [ClinicalItemType.TxOCSBiopsy]: 'TX OCS biopsy',
  [ClinicalItemType.LiverReconstructionQuestionnaire]: 'Reconstruction',
  [ClinicalItemType.LiverAnatomyQuestionnaire]: 'Anatomy',
  [ClinicalItemType.VitalUpdates]: 'Vitals',
  [ClinicalItemType.BloodCollection]: 'Blood collection',
  [ClinicalItemType.HeartPassOption]: 'Cooling and flush / Beating heart',
  [ClinicalItemType.PerfusionTerminationOption]: 'Perfusion termination option',
  [ClinicalItemType.DonorSpecimenHandoff]: 'Donor Specimen, Tissues, Paperwork',
  [ClinicalItemType.ServiceConfirmation]: 'Service confirmation form',
  [ClinicalItemType.SupplementalOrgan]: 'Supplement organs',
  [ClinicalItemType.DiscardConfirmationForm]: 'Service Outcome',
  [ClinicalItemType.Bile]: 'Bile',
  [ClinicalItemType.OrganMonitoring]: 'Organ Monitoring',
  [ClinicalItemType.MultipleDonorSpecimenHandoff]: 'Donor specimen, tissues, paperwork handoff',
  [ClinicalItemType.Randomization]: 'Randomization',
  [ClinicalItemType.KidneyRandomization]: 'Kidney Randomization',
  [ClinicalItemType.OrganOnSCS]: 'Organ on SCS',
  [ClinicalItemType.OffSCS]: 'Organ SCS Handoff',
  [ClinicalItemType.ScsTerminationOption]: 'SCS Handoff',
  [ClinicalItemType.IdentityAccessTicketGranted]: 'Achieve case access',
  [ClinicalItemType.IdentityAccessTicketRevoked]: 'Achieve case access',
  [ClinicalItemType.OrganApproved]: 'Organ Accepted',
  [ClinicalItemType.OrganDeclined]: 'Organ not Accepted',
};

export const radiometerNames: Record<RadiometerType, string> = {
  [RadiometerType.GC4Plus]: 'iStat CG4+',
  [RadiometerType.GC8Plus]: 'iStat CG8+',
  [RadiometerType.DonorRadiometer]: 'Donor Radiometer',
} as const;

export const shortRadiometerNames: Record<RadiometerType, string> = {
  [RadiometerType.GC4Plus]: 'CG4+',
  [RadiometerType.GC8Plus]: 'CG8+',
  [RadiometerType.DonorRadiometer]: 'Custom',
} as const;

export const bloodFromNames: Record<BloodFromType, string> = {
  [BloodFromType.LeftAtrium]: 'Left Atrium',
  [BloodFromType.ArterialLine]: 'Arterial Line',
  [BloodFromType.PulmonaryVeins]: 'Pulmonary Veins',
} as const;

export enum ClinicalLogSortTypes {
  timeForward = 'timeForward',
  timeReverse = 'timeReverse',
}

type ClinicalDataGroupedItems = {
  [key in ClinicalItemType]?: { sortType: ClinicalLogSortTypes };
};

export const clinicalDataGroupedItems: ClinicalDataGroupedItems = {
  [ClinicalItemType.VitalUpdates]: {
    sortType: ClinicalLogSortTypes.timeReverse,
  },
};

export const liverAnatomyDescription: Record<LiverAnatomyType, string> = {
  [LiverAnatomyType.Normal]: 'Normal',
  [LiverAnatomyType.LGA]: 'Replaced / accessory left from LGA, no reconstruction required',
  [LiverAnatomyType.Aorta]: 'Replaced / accessory left from Aorta, needs reconstruction',
  [LiverAnatomyType.RightSMA]: 'Replaced / accessory right, from SMA, needs reconstruction',
  [LiverAnatomyType.Complex]:
    'Replaced / accessory right, from SMA, needs reconstruction and replaced/accessory left from LGA, needs reconstruction',
  [LiverAnatomyType.SMA]: 'Totally replaced system from SMA',
  [LiverAnatomyType.Other]: 'Other',
};

export const wolsNames: Record<WOLSType, string> = {
  [WOLSType.OR]: 'Aorta',
  [WOLSType.PACU]: 'PACU',
  [WOLSType.ICU]: 'ICU',
  [WOLSType.Other]: 'Other',
};

export const vitalsNames: Record<string, string> = {
  bp: 'BP',
  hr: 'HR',
  rr: 'RR',
  map: 'MAP',
  sat: 'Sat',
};

export const perfusionTerminationNames: Record<DisplayedPerfusionTerminationType, string> = {
  [PerfusionTerminationType.doubleLungFlush]: 'Double lung flush',
  [PerfusionTerminationType.firstLungFlush]: 'First lung flush',
  [PerfusionTerminationType.secondLungFlush]: 'Second lung flush',
};

export const pfRatioInSituNames: Record<string, string> = {
  pfRatioOnOxygen: 'PF Ratio on 100% oxygen',
  bloodFrom: 'Blood from',
  radiometer: 'Radiometer',
  paO2: 'PaO2, mmHg',
  pvO2: 'PvO2, mmHg',
  peep: 'PEEP (cmH₂0)',
  vt: 'Vt (ml)',
  averagePfRatio: 'Average PF Ratio',
  lupv: 'LUPV',
  llpv: 'LLPV',
  rupv: 'RUPV',
  rlpv: 'RLPV',
};

export const pfRatioOnOCSNames: Record<string, string> = {
  pfRatioOnOxygen: 'PF Ratio on 21% oxygen',
  radiometer: 'Radiometer',
  paO2: 'PaO2, mmHg',
  pvO2: 'PvO2, mmHg',
};

export const bloodSampleNames: BloodSampleNames = {
  commonMeasurement: 'Lactate',
  firstSection: {
    temperature: 'Temperature 1',
    pH: 'pH',
    pcO2: 'PCO2, mmHg',
    paO2: 'PO2, mmHg',
    pvO2: 'PvO2, mmHg',
    bEecf: 'BEecf, mmol/L',
    hcO3: 'HCO3, mmol/L',
    tcO2: 'TCO2, mmol/L',
    saO2: 'sO2, %',
  },
  secondSection: {
    temperature: 'Temperature 2',
    pH: 'pH',
    pcO2: 'PCO2, mmHg',
    pO2: 'PO2, mmHg',
  },
  thirdSection: {
    na: 'Na, mmol/L',
    k: 'K, mmol/L',
    iCA: 'iCA, mmol/L',
    glu: 'Glu, mg/dL',
    hct: 'Hct, %/pCV',
    hb: 'Hb, g/dL',
  },
};

export const CCT_DANGER_THRESHOLD_MINUTES = 150;

export const CCT_WINDOW_STOP_ITEM_TYPES: ClinicalItemType[] = [
  ClinicalItemType.OrganOnOCS,
  ClinicalItemType.OrganOnSCS,
  ClinicalItemType.OrganDeclined,
];
